/*───────────────────────────────────────────────────────────────
 🌐 Project: Ring2All Platform
 🧑‍💻 Author: Rodrigo Cuadra
 🏢 Company: Ring2All LLC
 📄 File: api/arsApi.ts (ARS - Route Selections)
 🗓️ Created: 2025-01-15
 🧩 Description:
    API service layer for ARS (Automatic Route Selection).
    Centralizes all API calls for better maintainability.

────────────────────────────────────────────────────────────────*/

import { buildApiUrl, getApiHeaders } from '../../../config/api';
import {
  ListArsProfilesParams,
  ListArsProfilesResponse,
  ArsProfile,
  OutboundRouteOption,
  TimeGroupOption
} from '../types';

/**
 * API endpoints
 */
export const ARS_ENDPOINT = '/api/telephony/ars';
export const OUTBOUND_ROUTES_ENDPOINT = '/api/telephony/outbound-routes';
export const TIME_GROUPS_ENDPOINT = '/api/telephony/time-groups';

/**
 * Normalize headers for fetch
 */
function normalizeHeaders(headers?: HeadersInit): Record<string, string> {
  if (!headers) {
    return {};
  }

  if (headers instanceof Headers) {
    return Object.fromEntries(headers.entries());
  }

  if (Array.isArray(headers)) {
    return headers.reduce<Record<string, string>>((accumulator, [key, value]) => {
      accumulator[key] = value;
      return accumulator;
    }, {});
  }

  return headers;
}

/**
 * Request ARS API with error handling
 */
async function requestArsApi<T>(
  endpoint: string,
  fallbackMessage: string,
  options: RequestInit = {}
): Promise<{ success: true; data: T; message?: string; pagination?: { page: number; limit: number; total: number; totalPages: number } }> {
  const response = await fetch(
    buildApiUrl(endpoint),
    {
      ...options,
      headers: {
        ...getApiHeaders(),
        ...normalizeHeaders(options.headers),
        ...(options.body && { 'Content-Type': 'application/json' })
      }
    }
  );

  let payload: unknown;

  try {
    payload = await response.json();
  } catch {
    payload = undefined;
  }

  if (!response.ok) {
    const message = payload && typeof payload === 'object' && 'message' in payload
      ? ((payload as Record<string, unknown>).message as string | undefined)
      : fallbackMessage;
    throw new Error(message || fallbackMessage);
  }

  if (!payload || (typeof payload === 'object' && 'success' in payload && (payload as Record<string, unknown>).success !== true)) {
    const message = payload && typeof payload === 'object' && 'message' in payload
      ? ((payload as Record<string, unknown>).message as string | undefined)
      : fallbackMessage;
    throw new Error(message || fallbackMessage);
  }

  if (payload && typeof payload === 'object' && 'success' in payload && (payload as Record<string, unknown>).success) {
    return payload as { success: true; data: T; message?: string; pagination?: { page: number; limit: number; total: number; totalPages: number } };
  }

  return {
    success: true,
    data: undefined as T
  };
}

/**
 * Fetch ARS profiles list
 */
export async function fetchArsProfiles(
  params: ListArsProfilesParams
): Promise<ListArsProfilesResponse> {
  const queryParams = new URLSearchParams({
    page: params.page.toString(),
    limit: params.limit.toString(),
    sortBy: params.sortBy,
    sortOrder: params.sortOrder
  });

  if (params.search) {
    queryParams.set('search', params.search);
  }

  if (params.domainId != null) {
    queryParams.set('domainId', params.domainId.toString());
  }

  return requestArsApi<ListArsProfilesResponse>(
    `${ARS_ENDPOINT}?${queryParams.toString()}`,
    'Failed to fetch ARS profiles'
  ).then(result => ({
    success: result.success,
    data: result.data.data || [],
    pagination: result.data.pagination || result.pagination || {
      page: params.page,
      limit: params.limit,
      total: 0,
      totalPages: 0
    }
  }));
}

/**
 * Fetch single ARS profile by ID
 */
export async function fetchArsProfileById(
  id: string | number
): Promise<ArsProfile> {
  const result = await requestArsApi<ArsProfile>(
    `${ARS_ENDPOINT}/${id}`,
    'Failed to fetch ARS profile'
  );
  return result.data;
}

/**
 * Create ARS profile
 */
export async function createArsProfile(
  payload: {
    name: string;
    description?: string | null;
    enabled?: boolean;
    routes?: Array<{
      outboundRouteId: number;
      timeGroupId?: number | null;
      priority: number;
      enabled?: boolean;
    }>;
  }
): Promise<ArsProfile> {
  const result = await requestArsApi<ArsProfile>(
    ARS_ENDPOINT,
    'Failed to create ARS profile',
    {
      method: 'POST',
      body: JSON.stringify(payload)
    }
  );
  if (!result.data) {
    throw new Error('Failed to create ARS profile - no data returned');
  }
  return result.data;
}

/**
 * Update ARS profile
 */
export async function updateArsProfile(
  id: string | number,
  payload: {
    name?: string;
    description?: string | null;
    enabled?: boolean;
    routes?: Array<{
      outboundRouteId: number;
      timeGroupId?: number | null;
      priority: number;
      enabled?: boolean;
    }>;
  }
): Promise<ArsProfile> {
  const result = await requestArsApi<ArsProfile>(
    `${ARS_ENDPOINT}/${id}`,
    'Failed to update ARS profile',
    {
      method: 'PUT',
      body: JSON.stringify(payload)
    }
  );
  if (!result.data) {
    throw new Error('Failed to update ARS profile - no data returned');
  }
  return result.data;
}

/**
 * Delete ARS profile by ID
 */
export async function deleteArsProfileById(
  id: string | number
): Promise<void> {
  await requestArsApi<null>(
    `${ARS_ENDPOINT}/${id}`,
    'Failed to delete ARS profile',
    {
      method: 'DELETE'
    }
  );
}

/**
 * Fetch outbound routes for dropdowns
 */
export async function fetchOutboundRoutes(
  limit: number = 100
): Promise<OutboundRouteOption[]> {
  const result = await requestArsApi<{ data: OutboundRouteOption[] }>(
    `${OUTBOUND_ROUTES_ENDPOINT}?page=1&limit=${limit}&sortBy=name&sortOrder=asc`,
    'Failed to fetch outbound routes'
  );
  return Array.isArray(result.data.data) ? result.data.data : [];
}

/**
 * Fetch time groups for dropdowns
 */
export async function fetchTimeGroups(
  limit: number = 100
): Promise<TimeGroupOption[]> {
  const result = await requestArsApi<{ data: TimeGroupOption[] }>(
    `${TIME_GROUPS_ENDPOINT}?page=1&limit=${limit}&sortBy=name&sortOrder=asc`,
    'Failed to fetch time groups'
  );
  return Array.isArray(result.data.data) ? result.data.data : [];
}

